import { useState, useContext } from "react";
import { AppContext } from "@/App";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageCircle, CreditCard, Banknote, CheckCircle2, MapPin } from "lucide-react";

const VEHICLE_IDS = ["eclass", "sclass", "vclass"];

export default function BookingWidget() {
  const { t, lang } = useContext(AppContext);
  const [form, setForm] = useState({ pickup: "", dropoff: "", date: "", time: "", passengers: 1, vehicle: "eclass" });
  const [quote, setQuote] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(null);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setC = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  const calculate = async () => {
    if (!form.pickup || !form.dropoff || !form.date || !form.time) {
      toast.error(t.booking.errFields);
      return;
    }
    setLoading("calc");
    try {
      const res = await api.post("/quote", { ...form, passengers: Number(form.passengers) });
      setQuote(res.data);
    } catch {
      toast.error("Error");
    } finally {
      setLoading(null);
    }
  };

  const validContact = () => {
    if (!contact.name || !contact.email || !contact.phone) {
      toast.error(t.booking.errContact);
      return false;
    }
    return true;
  };

  const createBooking = async (method) => {
    const res = await api.post("/bookings", {
      ...form, ...contact, passengers: Number(form.passengers), payment_method: method,
    });
    return res.data;
  };

  const payOnline = async () => {
    if (!quote || !validContact()) return;
    setLoading("online");
    try {
      const booking = await createBooking("online");
      const res = await api.post("/payments/checkout", {
        booking_id: booking.booking_id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Error");
      setLoading(null);
    }
  };

  const payOnboard = async () => {
    if (!quote || !validContact()) return;
    setLoading("onboard");
    try {
      const booking = await createBooking("onboard");
      setDone(booking);
    } catch {
      toast.error("Error");
    } finally {
      setLoading(null);
    }
  };

  const whatsappBook = () => {
    const msg = `${t.booking.title}%0A${encodeURIComponent(form.pickup)} → ${encodeURIComponent(form.dropoff)}%0A${form.date} ${form.time} · ${t.vehicles[form.vehicle].name}${quote ? `%0A€${quote.total}` : ""}`;
    window.open(`https://wa.me/393482215105?text=${msg}`, "_blank");
  };

  if (done) {
    return (
      <div data-testid="booking-confirmed" className="card-surface p-8 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-[#D4AF37]" />
        <h3 className="font-display text-2xl font-semibold text-white">{t.booking.confirmedTitle}</h3>
        <p className="mt-2 text-sm text-neutral-400">{t.booking.confirmedSub}</p>
        <div className="mx-auto mt-6 max-w-xs space-y-2 rounded-lg bg-[#0B0C0E] p-4 text-left text-sm">
          <div className="flex justify-between"><span className="text-neutral-500">{t.booking.route}</span><span className="text-neutral-200">{done.route_label}</span></div>
          <div className="flex justify-between"><span className="text-neutral-500">{t.booking.date}</span><span className="text-neutral-200">{done.date} · {done.time}</span></div>
          <div className="flex justify-between border-t border-[rgba(212,175,55,0.15)] pt-2"><span className="text-neutral-500">{t.booking.total}</span><span className="font-semibold text-[#E5C158]">€{done.amount.toFixed(2)}</span></div>
        </div>
        <button data-testid="new-booking-btn" onClick={() => { setDone(null); setQuote(null); }} className="btn-outline-gold mt-6">
          {t.booking.newBooking}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="booking-widget" className="card-surface p-6 sm:p-8">
      <h3 className="font-display text-2xl font-semibold text-white">{t.booking.title}</h3>
      <p className="mt-1 text-sm text-neutral-400">{t.booking.subtitle}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.pickup}</label>
          <input data-testid="booking-pickup-input" list="rpl-places" value={form.pickup} onChange={set("pickup")} placeholder={t.booking.pickupPh} className="input-dark" />
        </div>
        <div>
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.dropoff}</label>
          <input data-testid="booking-dropoff-input" list="rpl-places" value={form.dropoff} onChange={set("dropoff")} placeholder={t.booking.dropoffPh} className="input-dark" />
        </div>
        <datalist id="rpl-places">
          {t.booking.suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
        <div>
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.date}</label>
          <input data-testid="booking-date-picker" type="date" value={form.date} onChange={set("date")} className="input-dark [color-scheme:dark]" />
        </div>
        <div>
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.time}</label>
          <input data-testid="booking-time-select" type="time" value={form.time} onChange={set("time")} className="input-dark [color-scheme:dark]" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.vehicle}</label>
          <div data-testid="booking-vehicle-select" className="grid grid-cols-3 gap-2">
            {VEHICLE_IDS.map((id) => (
              <button
                key={id}
                type="button"
                data-testid={`vehicle-option-${id}`}
                onClick={() => { setForm({ ...form, vehicle: id }); setQuote(null); }}
                className={`rounded-lg border px-2 py-3 text-center transition-all duration-200 ${
                  form.vehicle === id
                    ? "border-[#D4AF37] bg-[rgba(212,175,55,0.1)]"
                    : "border-[rgba(212,175,55,0.15)] bg-[#0B0C0E] hover:border-[rgba(212,175,55,0.4)]"
                }`}
              >
                <span className="block font-display text-sm font-semibold text-white">{t.vehicles[id].name.replace("Mercedes ", "")}</span>
                <span className="mt-0.5 block text-[10px] text-neutral-500">{t.vehicles[id].desc}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label-accent mb-1.5 block !text-[10px]">{t.booking.passengers}</label>
          <select data-testid="booking-passengers-select" value={form.passengers} onChange={set("passengers")} className="input-dark">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <button data-testid="calculate-instant-price-btn" onClick={calculate} disabled={loading === "calc"} className="btn-gold mt-6 w-full">
        {loading === "calc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
        {loading === "calc" ? t.booking.calculating : t.booking.calc}
      </button>

      {quote && (
        <div data-testid="quote-result" className="mt-6 rounded-lg border border-[rgba(212,175,55,0.3)] bg-[#0B0C0E] p-5">
          <div className="flex items-baseline justify-between">
            <span className="label-accent !text-[10px]">{t.booking.quoteTitle}</span>
            <span className="font-code text-[10px] uppercase tracking-widest text-neutral-500">{t.booking.fixed}</span>
          </div>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">{t.booking.route}</span><span className="text-neutral-300">{quote.route_label}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">{t.booking.vehicleClass}</span><span className="text-neutral-300">{quote.vehicle_label[lang]}</span></div>
            {quote.night && quote.night_pct > 0 && (
              <div className="flex justify-between"><span className="text-neutral-500">{t.booking.night}</span><span className="text-amber-400">+{quote.night_pct}%</span></div>
            )}
            {quote.holiday && quote.holiday_pct > 0 && (
              <div className="flex justify-between"><span className="text-neutral-500">{t.booking.holiday}</span><span className="text-amber-400">+{quote.holiday_pct}%</span></div>
            )}
            <div className="flex justify-between border-t border-[rgba(212,175,55,0.15)] pt-2">
              <span className="font-medium text-neutral-300">{t.booking.total}</span>
              <span data-testid="quote-total" className="font-display text-2xl font-semibold text-[#E5C158]">€{quote.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <input data-testid="booking-name-input" value={contact.name} onChange={setC("name")} placeholder={t.booking.name} className="input-dark" />
            <input data-testid="booking-email-input" type="email" value={contact.email} onChange={setC("email")} placeholder={t.booking.email} className="input-dark" />
            <input data-testid="booking-phone-input" value={contact.phone} onChange={setC("phone")} placeholder={t.booking.phone} className="input-dark" />
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <button data-testid="stripe-checkout-pay-btn" onClick={payOnline} disabled={loading === "online"} className="btn-gold !px-3">
              {loading === "online" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              {t.booking.payOnline}
            </button>
            <button data-testid="pay-onboard-btn" onClick={payOnboard} disabled={loading === "onboard"} className="btn-outline-gold !px-3">
              {loading === "onboard" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
              {t.booking.payOnboard}
            </button>
            <button data-testid="whatsapp-quick-contact-btn" onClick={whatsappBook} className="inline-flex items-center justify-center gap-2 rounded-full border border-[rgba(37,211,102,0.4)] px-3 py-3 text-sm font-semibold text-[#25D366] transition-all duration-300 hover:bg-[rgba(37,211,102,0.08)]">
              <MessageCircle className="h-4 w-4" />
              {t.booking.whatsapp}
            </button>
          </div>
          <p className="mt-3 text-center text-[11px] text-neutral-500">{t.booking.secure}</p>
        </div>
      )}
    </div>
  );
}
